import React, { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableRow,
} from "@/components/ui/table"
import { useAuth } from '@/context/auth';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { 
  CreditCard, 
  User, 
  DollarSign, 
  TrendingUp, 
  FileText, 
  Users,
  Settings,
  Lock,
  ShieldCheck,
  Image,
  MessageSquare,
  Star,
  Sparkles
} from 'lucide-react';
import { useLanguage } from '@/context/language-context';
import { useLanguageUtils } from '@/context/language-context';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [adminStats, setAdminStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    newUsersThisMonth: 0,
    totalRevenue: 0,
  });
  const [escortStats, setEscortStats] = useState({
    profileViews: 0,
    messagesReceived: 0,
    bookingsConfirmed: 0,
    averageRating: 0,
  });
  const [clientStats, setClientStats] = useState({
    profilesViewed: 0,
    messagesSent: 0,
    transactions: [{ id: '1', date: new Date(), amount: '100' }],
    favoriteProfiles: 0,
  });
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getUrlWithLanguage } = useLanguageUtils();
  const { t } = useLanguage();

  useEffect(() => {
    if (user?.role === 'escort') {
      console.log("User is escort, redirecting to escort dashboard...");
      navigate(getUrlWithLanguage('/escort-dashboard'), { replace: true });
    }
  }, [user, navigate, getUrlWithLanguage]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await new Promise(resolve => setTimeout(resolve, 1000));

        if (user?.role === 'admin') {
          setAdminStats({
            totalUsers: 150,
            activeUsers: 120,
            newUsersThisMonth: 15,
            totalRevenue: 50000,
          });
        }

        if (user?.role === 'escort') {
          setEscortStats({
            profileViews: 520,
            messagesReceived: 75,
            bookingsConfirmed: 32,
            averageRating: 4.7,
          });
        }

        if (user?.role === 'client') {
          setClientStats({
            profilesViewed: 85,
            messagesSent: 42,
            transactions: [
              { id: '1', date: new Date(), amount: '50' },
              { id: '2', date: new Date(), amount: '75' },
              { id: '3', date: new Date(), amount: '120' },
            ],
            favoriteProfiles: 12,
          });
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load dashboard data. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    if (user && user.role !== 'escort') {
      fetchData();
    } else if (!user) {
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [user, toast, t]);

  if (user?.role === 'escort') {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          <p className="ml-4 text-muted-foreground">Redirecting to Escort Dashboard...</p>
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div className="py-8 px-4 bg-muted/30 min-h-screen">
          <div className="max-w-7xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4">Unauthorized Access</h2>
            <p className="text-muted-foreground mb-6">You must be logged in to view this page.</p>
            <Button onClick={() => navigate('/login')}>Go to Login</Button>
          </div>
        </div>
      </Layout>
    );
  }

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
      toast({
        title: 'Logged out',
        description: 'You have been successfully logged out.',
      });
    } catch (error) {
      console.error('Logout failed:', error);
      toast({
        title: 'Error',
        description: 'Failed to logout. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const totalSpent = clientStats.transactions.reduce((total, transaction) => total + Number(transaction.amount), 0);
  const bookingRate = escortStats.profileViews > 0 ? (escortStats.bookingsConfirmed / escortStats.profileViews) * 100 : 0;
  const userGrowthRate = adminStats.totalUsers > 0 ? (adminStats.newUsersThisMonth / adminStats.totalUsers) * 100 : 0;

  return (
    <Layout>
      <div className="py-8 px-4 bg-muted/30 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold mb-8">
            {t('dashboard.welcome', { name: user.displayName || user.email || 'User' })}!
          </h1>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="bg-card rounded-lg h-[150px]"></div>
              ))}
            </div>
          ) : (
            <>
              {user.role === 'admin' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span className="text-lg font-semibold">Total Users</span>
                        <Users size={20} className="text-muted-foreground" />
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">{adminStats.totalUsers}</div>
                      <Progress value={userGrowthRate} className="mt-2" />
                      <p className="text-sm text-muted-foreground mt-1">
                        {adminStats.activeUsers} active users
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span className="text-lg font-semibold">New Users This Month</span>
                        <User size={20} className="text-muted-foreground" />
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">{adminStats.newUsersThisMonth}</div>
                      <Progress value={userGrowthRate} className="mt-2" />
                      <p className="text-sm text-muted-foreground mt-1">
                        {userGrowthRate.toFixed(1)}% growth
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span className="text-lg font-semibold">Total Revenue</span>
                        <DollarSign size={20} className="text-muted-foreground" />
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">€{adminStats.totalRevenue}</div>
                      <Progress value={65} className="mt-2" />
                      <p className="text-sm text-muted-foreground mt-1">
                        65% of target achieved
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {user.role === 'client' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span className="text-lg font-semibold">Profiles Viewed</span>
                        <TrendingUp size={20} className="text-muted-foreground" />
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">{clientStats.profilesViewed}</div>
                      <Progress value={60} className="mt-2" />
                      <p className="text-sm text-muted-foreground mt-1">
                        60% increase this month
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span className="text-lg font-semibold">Messages Sent</span>
                        <FileText size={20} className="text-muted-foreground" />
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">{clientStats.messagesSent}</div>
                      <Progress value={45} className="mt-2" />
                      <p className="text-sm text-muted-foreground mt-1">
                        45 new messages this month
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span className="text-lg font-semibold">Total Spent</span>
                        <CreditCard size={20} className="text-muted-foreground" />
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">€{totalSpent}</div>
                      <Progress value={80} className="mt-2" />
                      <p className="text-sm text-muted-foreground mt-1">
                        80% of budget used
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {user.role === 'client' && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold mb-4">Transaction History</h2>
                  <Table>
                    <TableCaption>A summary of your recent transactions.</TableCaption>
                    <TableHead>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Amount</TableHead>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clientStats.transactions.map((transaction) => (
                        <TableRow key={transaction.id}>
                          <TableCell>{transaction.date.toLocaleDateString()}</TableCell>
                          <TableCell>€{transaction.amount}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {user.role !== 'admin' && (
                <Card className="mt-8 col-span-1 md:col-span-2 lg:col-span-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-lg font-semibold">
                      {t('dashboard.exclusive_subscriptions')}
                    </CardTitle>
                    <Sparkles className="h-5 w-5 text-white/80" />
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-white/90 mb-4">
                      {t('dashboard.subscriptions_description')}
                    </p>
                    <Button
                      variant="secondary"
                      className="bg-white text-pink-600 hover:bg-gray-100"
                      onClick={() => navigate(getUrlWithLanguage('/escort-subscription'))}
                    >
                      {t('dashboard.view_plans')}
                    </Button>
                  </CardContent>
                </Card>
              )}

              <div className="mt-12 text-center">
                <Button variant="outline" onClick={handleLogout}>
                  {t('navbar.logout')}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
